import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "@ui5/webcomponents/dist/Table.js";
import "@ui5/webcomponents/dist/TableRow.js";
import "@ui5/webcomponents/dist/TableCell.js";
import "@ui5/webcomponents/dist/TableHeaderRow.js";
import "@ui5/webcomponents/dist/TableHeaderCell.js";
import "@ui5/webcomponents/dist/Toast.js";
import "@ui5/webcomponents/dist/Bar.js";

const UserMaintenance = () => {
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [filteredBarangays, setFilteredBarangays] = useState([]); // New state for filtered barangays
  const [filters, setFilters] = useState({
    region: "",
    province: "",
    municipality: "",
    barangay: "",
  });
  const [adminMode, setAdminMode] = useState(null); // null | 'province' | 'municipality' | 'barangay' | 'encoder'
  const toastRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const userRole = parseInt(localStorage.getItem("userRole"), 10);
    // Redirect if the user role is not 1
    if (userRole !== 1) {
      navigate('/');
    }
  }, [navigate]);

  const showToast = (message) => {
    if (toastRef.current) {
      toastRef.current.textContent = message;
      toastRef.current.open = true;
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/locations/api/regions`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      if (!response.ok) throw new Error("Error fetching regions.");
      const data = await response.json();
      setRegions(data);
    } catch (error) {
      showToast("Failed to fetch regions.");
    }
  };

  const fetchProvinces = async (regionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/locations/api/provinces?region_id=${regionId}`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      if (!response.ok) throw new Error("Error fetching provinces.");
      const data = await response.json();
      setProvinces(data);
    } catch (error) {
      showToast("Failed to fetch provinces.");
    }
  };

  const fetchMunicipalities = async (provinceId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/locations/api/municipalities?province_id=${provinceId}`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      if (!response.ok) throw new Error("Error fetching municipalities.");
      const data = await response.json();
      setMunicipalities(data);
    } catch (error) {
      showToast("Failed to fetch municipalities.");
    }
  };

  const fetchBarangays = async (municipalityId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/locations/api/barangays-with-details?region_id=${filters.region}&province_id=${filters.province}&municipality_id=${municipalityId}`,
        {
          headers: { "x-api-key": process.env.REACT_APP_API_KEY },
        }
      );
      if (!response.ok) throw new Error("Error fetching barangays.");
      const data = await response.json();
      setBarangays(data); // Directly set the data without additional mapping
      setFilteredBarangays(data); // Initialize filtered barangays with all barangays
    } catch (error) {
      showToast("Failed to fetch barangays.");
    }
  };

  useEffect(() => {
    if (location.state && location.state.filters) {
      const restoredFilters = location.state.filters;
      setFilters(restoredFilters);

      (async () => {
        await fetchRegions();
        if (restoredFilters.region) {
          await fetchProvinces(restoredFilters.region);
        }
        if (restoredFilters.province) {
          await fetchMunicipalities(restoredFilters.province);
        }
        if (restoredFilters.municipality) {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/locations/api/barangays-with-details?region_id=${restoredFilters.region}&province_id=${restoredFilters.province}&municipality_id=${restoredFilters.municipality}`,
            {
              headers: { "x-api-key": process.env.REACT_APP_API_KEY },
            }
          );
          if (response.ok) {
            const data = await response.json();
            setBarangays(data);
            if (restoredFilters.barangay) {
              const filtered = data.filter(
                (barangay) => barangay.barangay_id === Number(restoredFilters.barangay)
              );
              setFilteredBarangays(filtered);
            } else {
              setFilteredBarangays(data);
            }
          } else {
            setBarangays([]);
            setFilteredBarangays([]);
          }
        }
      })();
    } else {
      fetchRegions();
    }
    // eslint-disable-next-line
  }, []);

  const handleRegionChange = (e) => {
    const regionId = e.target.value;
    setFilters({ region: regionId, province: "", municipality: "", barangay: "" });
    setProvinces([]);
    setMunicipalities([]);
    setBarangays([]);
    setFilteredBarangays([]);
    if (regionId) fetchProvinces(regionId);
  };

  const handleProvinceChange = (e) => {
    const provinceId = e.target.value;
    setFilters((prev) => ({ ...prev, province: provinceId, municipality: "", barangay: "" }));
    setMunicipalities([]);
    setBarangays([]);
    setFilteredBarangays([]);
    if (provinceId) fetchMunicipalities(provinceId);
  };

  const handleMunicipalityChange = (e) => {
    const municipalityId = e.target.value;
    setFilters((prev) => ({ ...prev, municipality: municipalityId, barangay: "" }));
    setBarangays([]);
    setFilteredBarangays([]);
    if (municipalityId) fetchBarangays(municipalityId);
  };

  const handleBarangayChange = (e) => {
    if (adminMode !== 'barangay' && adminMode !== 'encoder') return; // allow both modes
    const barangayId = Number(e.target.value); // Convert to number for comparison
    setFilters((prev) => ({ ...prev, barangay: barangayId }));

    // Filter barangays based on the selected barangay
    if (barangayId) {
      const selected = barangays.filter(b => b.barangay_id === barangayId);
      setFilteredBarangays(selected);
    } else {
      setFilteredBarangays(barangays);
    }
  };

  const handleRowClick = (id) => {
    // id can be province_id, municipality_id, or barangay_id depending on mode
    navigate(`/attach-email/${adminMode}/${id}`, { state: { filters, adminMode } });
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "1rem" }}>
      <h1>User Maintenance</h1>
      <p>Feature: Manage user data manually.</p>
      <ui5-toast ref={toastRef}></ui5-toast>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <ui5-button design={adminMode === 'province' ? 'Emphasized' : 'Transparent'} onClick={() => { setAdminMode('province'); setFilters({ region: '', province: '', municipality: '', barangay: '' }); setProvinces([]); setMunicipalities([]); setBarangays([]); setFilteredBarangays([]); }}>
          Add Provincial Admin
        </ui5-button>
        <ui5-button design={adminMode === 'municipality' ? 'Emphasized' : 'Transparent'} onClick={() => { setAdminMode('municipality'); setFilters({ region: '', province: '', municipality: '', barangay: '' }); setProvinces([]); setMunicipalities([]); setBarangays([]); setFilteredBarangays([]); }}>
          Add Municipal Admin
        </ui5-button>
        <ui5-button design={adminMode === 'barangay' ? 'Emphasized' : 'Transparent'} onClick={() => { setAdminMode('barangay'); setFilters({ region: '', province: '', municipality: '', barangay: '' }); setProvinces([]); setMunicipalities([]); setBarangays([]); setFilteredBarangays([]); }}>
          Add Barangay Admin
        </ui5-button>
        <ui5-button design={adminMode === 'encoder' ? 'Emphasized' : 'Transparent'} onClick={() => { setAdminMode('encoder'); setFilters({ region: '', province: '', municipality: '', barangay: '' }); setProvinces([]); setMunicipalities([]); setBarangays([]); setFilteredBarangays([]); }}>
          Add Encoders
        </ui5-button>
      </div>
      {/* Filter Bar */}
      <div style={{ width: "100%", maxWidth: "1200px", display: "flex", justifyContent: "center" }}>
        <ui5-bar design="Header" style={{ width: "100%", maxWidth: "800px" }}>
          <div slot="startContent" style={{ display: "flex", gap: "1rem", width: "100%" }}>
            {adminMode && (
              <select
                value={filters.region}
                onChange={handleRegionChange}
                style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", width: "180px" }}
              >
                <option value="">Select Region</option>
                {regions.map((region) => (
                  <option key={region.region_id} value={region.region_id}>
                    {region.region_name || "Unknown Region"}
                  </option>
                ))}
              </select>
            )}
            {adminMode && (adminMode !== 'province') && (
              <select
                value={filters.province}
                onChange={handleProvinceChange}
                disabled={!filters.region}
                style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", width: "180px" }}
              >
                <option value="">Select Province</option>
                {provinces.map((province) => (
                  <option key={province.province_id} value={province.province_id}>
                    {province.province_name}
                  </option>
                ))}
              </select>
            )}
            {adminMode && (adminMode === 'barangay' || adminMode === 'encoder') && (
              <select
                value={filters.municipality}
                onChange={handleMunicipalityChange}
                disabled={!filters.province}
                style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", width: "180px" }}
              >
                <option value="">Select Municipality</option>
                {municipalities.map((municipality) => (
                  <option key={municipality.municipality_id} value={municipality.municipality_id}>
                    {municipality.municipality_name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </ui5-bar>
      </div>

      {/* Table */}
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          overflowX: "auto",
          boxShadow: "0 0 0.125rem 0 rgba(0,0,0,0.1)",
          borderRadius: "0.25rem",
        }}
      >
        <ui5-table id="table" no-data-text="No data available" style={{ width: "100%" }}>
          <ui5-table-header-row slot="headerRow">
            {adminMode === 'province' && (
              <>
                <ui5-table-header-cell>Province</ui5-table-header-cell>
              </>
            )}
            {adminMode === 'municipality' && (
              <>
                <ui5-table-header-cell>Municipality</ui5-table-header-cell>
              </>
            )}
            {(adminMode === 'barangay' || adminMode === 'encoder') && (
              <>
                <ui5-table-header-cell>Barangay</ui5-table-header-cell>
              </>
            )}
          </ui5-table-header-row>

          {adminMode === 'province' && filters.region && provinces.map(p => (
            <ui5-table-row key={p.province_id} row-key={p.province_id} onClick={() => handleRowClick(p.province_id)} style={{ cursor: 'pointer' }}>
              <ui5-table-cell>{p.province_name}</ui5-table-cell>
            </ui5-table-row>
          ))}

          {adminMode === 'municipality' && filters.province && municipalities.map(m => (
            <ui5-table-row key={m.municipality_id} row-key={m.municipality_id} onClick={() => handleRowClick(m.municipality_id)} style={{ cursor: 'pointer' }}>
              <ui5-table-cell>{provinces.find(p=>p.province_id==m.province_id)?.province_name || ''}</ui5-table-cell>
              <ui5-table-cell>{m.municipality_name}</ui5-table-cell>
            </ui5-table-row>
          ))}

          {(adminMode === 'barangay' || adminMode === 'encoder') && filters.municipality && barangays.map(b => (
            <ui5-table-row key={b.barangay_id} row-key={b.barangay_id} onClick={() => handleRowClick(b.barangay_id)} style={{ cursor: 'pointer' }}>
              <ui5-table-cell>{provinces.find(p=>p.province_id==b.province_id)?.province_name || ''}</ui5-table-cell>
              <ui5-table-cell>{municipalities.find(m=>m.municipality_id==b.municipality_id)?.municipality_name || ''}</ui5-table-cell>
              <ui5-table-cell>{b.barangay_name}</ui5-table-cell>
            </ui5-table-row>
          ))}
        </ui5-table>
      </div>
    </div>
  );
};

export default UserMaintenance;
